// src/pages/ClientTasks.tsx

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../firebase/config';
import { collection, getDocs, getDoc, doc, updateDoc, query, where, orderBy } from 'firebase/firestore';
import { useUserRole } from '../hooks/useUserRole';
import Navigation from '../components/Navigation';

interface TaskAttachment {
  id: string;
  url: string;
  fileName: string;
  fileSize: number;
  uploadedBy: string;
  uploadedByName?: string;
  uploadedAt: any;
}

interface Task {
  id: string;
  title: string;
  description?: string;
  clientId: string;
  assignedBy: string;
  assignedByName?: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'not_started' | 'in_progress' | 'completed' | 'rejected';
  category?: string;
  dueDate?: any;
  comments?: TaskComment[];
  attachments?: TaskAttachment[];
  sequenceOrder?: number; // Sequential order for task completion
  createdAt?: any;
  updatedAt?: any;
}

interface TaskComment {
  id: string;
  userId: string;
  userName?: string;
  comment: string;
  createdAt: any;
}

const ClientTasks: React.FC = () => {
  const { user, role, loading: roleLoading } = useUserRole();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [newComment, setNewComment] = useState('');
  const [uploadingAttachment, setUploadingAttachment] = useState(false);

  const loadTasks = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      const tasksRef = collection(db, 'tasks');
      const tasksQuery = query(
        tasksRef, 
        where('clientId', '==', user.uid),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(tasksQuery);
      
      const tasksData: Task[] = [];
      snapshot.forEach((doc) => {
        tasksData.push({
          id: doc.id,
          ...doc.data()
        } as Task);
      });
      
      // Sort tasks by sequenceOrder (if present), then by creation date
      tasksData.sort((a, b) => {
        if (a.sequenceOrder !== undefined && b.sequenceOrder !== undefined) {
          return a.sequenceOrder - b.sequenceOrder;
        }
        if (a.sequenceOrder !== undefined) return -1;
        if (b.sequenceOrder !== undefined) return 1;
        // If no sequence order, sort by creation date (newest first)
        const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0);
        const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0);
        return bDate.getTime() - aDate.getTime();
      });
      
      setTasks(tasksData);
    } catch (error: any) {
      console.error('Error loading tasks:', error);
      setError('Failed to load tasks: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!roleLoading && role !== 'client') {
      navigate('/client-dashboard');
    }
  }, [roleLoading, role, navigate]);

  useEffect(() => {
    if (role === 'client' && user) {
      loadTasks();
    }
  }, [role, user, loadTasks]);

  const handleStatusUpdate = async (taskId: string, newStatus: 'not_started' | 'in_progress' | 'completed' | 'rejected') => {
    try {
      setError('');
      setSuccess('');
      
      const taskRef = doc(db, 'tasks', taskId);
      await updateDoc(taskRef, {
        status: newStatus,
        updatedAt: new Date()
      });

      setSuccess(`Task status updated to ${newStatus.replace('_', ' ')}`);
      await loadTasks();
      
      // Update selected task if it's the one being updated
      if (selectedTask && selectedTask.id === taskId) {
        setSelectedTask({ ...selectedTask, status: newStatus });
      }
    } catch (error: any) {
      console.error('Error updating task status:', error);
      setError('Failed to update task status: ' + error.message);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedTask) return;

    try {
      setError('');
      setSuccess('');
      
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setError('You must be signed in to add comments');
        return;
      }

      // Get user name
      let userName = currentUser.displayName || currentUser.email || 'Unknown';
      try {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          userName = (userData.firstName && userData.lastName ? `${userData.firstName} ${userData.lastName}` : userData.displayName) || userName;
        }
      } catch (err) {
        // Fallback to displayName or email
      }

      const comment: TaskComment = {
        id: Date.now().toString(),
        userId: currentUser.uid,
        userName,
        comment: newComment.trim(),
        createdAt: new Date()
      };

      const updatedComments = [...(selectedTask.comments || []), comment];
      const taskRef = doc(db, 'tasks', selectedTask.id);
      await updateDoc(taskRef, {
        comments: updatedComments,
        updatedAt: new Date()
      });

      setSelectedTask({ ...selectedTask, comments: updatedComments });
      setNewComment('');
      setSuccess('Comment added successfully!');
      await loadTasks();
    } catch (error: any) {
      console.error('Error adding comment:', error);
      setError('Failed to add comment: ' + error.message);
    }
  };

  const filteredTasks = tasks.filter((task) => {
    return statusFilter === 'all' || task.status === statusFilter;
  }).sort((a, b) => {
    // Sort by sequenceOrder if both have it, then by creation date
    if (a.sequenceOrder !== undefined && b.sequenceOrder !== undefined) {
      return a.sequenceOrder - b.sequenceOrder;
    }
    if (a.sequenceOrder !== undefined) return -1;
    if (b.sequenceOrder !== undefined) return 1;
    // If no sequence order, sort by creation date (newest first)
    const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0);
    const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0);
    return bDate.getTime() - aDate.getTime();
  });

  const stats = {
    total: tasks.length,
    notStarted: tasks.filter(t => t.status === 'not_started').length,
    inProgress: tasks.filter(t => t.status === 'in_progress').length,
    completed: tasks.filter(t => t.status === 'completed').length,
    rejected: tasks.filter(t => t.status === 'rejected').length
  };

  if (roleLoading || loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (role !== 'client') {
    return null;
  }

  return (
    <div>
      <Navigation />
      <div style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <h1>My Tasks</h1>
          <button
            onClick={() => navigate('/client-dashboard')}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            ← Back to Dashboard
          </button>
        </div>

        {/* Statistics Cards */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
          gap: '1rem',
          marginBottom: '2rem'
        }}>
          <StatCard title="Total Tasks" value={stats.total} color="#007bff" />
          <StatCard title="Not Started" value={stats.notStarted} color="#6c757d" />
          <StatCard title="In Progress" value={stats.inProgress} color="#ffc107" />
          <StatCard title="Completed" value={stats.completed} color="#28a745" />
          <StatCard title="Rejected" value={stats.rejected} color="#dc3545" />
        </div>

        {/* Alerts */}
        {error && (
          <div style={{
            backgroundColor: '#f8d7da',
            color: '#721c24',
            padding: '1rem',
            borderRadius: '4px',
            marginBottom: '1rem',
            border: '1px solid #f5c6cb'
          }}>
            {error}
          </div>
        )}
        {success && (
          <div style={{
            backgroundColor: '#d4edda',
            color: '#155724',
            padding: '1rem',
            borderRadius: '4px',
            marginBottom: '1rem',
            border: '1px solid #c3e6cb'
          }}>
            {success}
          </div>
        )}

        {/* Filter */}
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '1.5rem',
          borderRadius: '8px',
          marginBottom: '2rem'
        }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
            Filter by Status
          </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: '0.5rem',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              fontSize: '1rem',
              cursor: 'pointer'
            }}
          >
            <option value="all">All Statuses</option>
            <option value="not_started">Not Started</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        {/* Tasks List and Details */}
        <div style={{ display: 'grid', gridTemplateColumns: selectedTask ? '1fr 1fr' : '1fr', gap: '2rem' }}>
          {/* Tasks Table */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold' }}>Task</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold' }}>Priority</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold' }}>Status</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold' }}>Due Date</th>
                </tr>
              </thead>
              <tbody>
                {filteredTasks.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ padding: '2rem', textAlign: 'center', color: '#6c757d' }}>
                      {loading ? 'Loading tasks...' : 'No tasks found'}
                    </td>
                  </tr>
                ) : (
                  filteredTasks.map((task) => (
                    <tr 
                      key={task.id} 
                      onClick={() => setSelectedTask(task)}
                      style={{ 
                        borderBottom: '1px solid #dee2e6',
                        backgroundColor: selectedTask?.id === task.id ? '#e7f3ff' : 'white',
                        cursor: 'pointer'
                      }}
                    >
                      <td style={{ padding: '1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          {task.sequenceOrder !== undefined && task.sequenceOrder !== null && (
                            <div style={{
                              minWidth: '32px',
                              height: '32px',
                              borderRadius: '50%',
                              backgroundColor: '#007bff',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 'bold',
                              fontSize: '0.875rem'
                            }}>
                              {task.sequenceOrder}
                            </div>
                          )}
                          <div>
                            <div style={{ fontWeight: '500' }}>{task.title}</div>
                            {task.category && (
                              <div style={{ fontSize: '0.875rem', color: '#6c757d' }}>{task.category}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          backgroundColor: 
                            task.priority === 'urgent' ? '#dc3545' :
                            task.priority === 'high' ? '#fd7e14' :
                            task.priority === 'medium' ? '#ffc107' : '#28a745',
                          color: task.priority === 'medium' ? '#000' : '#fff'
                        }}>
                          {task.priority}
                        </span>
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          backgroundColor: 
                            task.status === 'completed' ? '#28a745' :
                            task.status === 'in_progress' ? '#ffc107' :
                            task.status === 'rejected' ? '#dc3545' : '#6c757d',
                          color: task.status === 'in_progress' ? '#000' : '#fff'
                        }}>
                          {task.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                        {task.dueDate?.toDate ? task.dueDate.toDate().toLocaleDateString() : 'No due date'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Task Details Panel */}
          {selectedTask && (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              padding: '2rem',
              position: 'sticky',
              top: '2rem',
              maxHeight: 'calc(100vh - 4rem)',
              overflowY: 'auto'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ margin: 0 }}>Task Details</h2>
                <button
                  onClick={() => setSelectedTask(null)}
                  style={{
                    padding: '0.5rem',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  ✕
                </button>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#495057' }}>Task Information</h3>
                <div style={{ marginBottom: '0.75rem' }}>
                  <div style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.25rem' }}>Title</div>
                  <div style={{ fontSize: '1rem', fontWeight: '500' }}>{selectedTask.title}</div>
                </div>
                {selectedTask.description && (
                  <div style={{ marginBottom: '0.75rem' }}>
                    <div style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.25rem' }}>Description</div>
                    <div style={{ fontSize: '1rem' }}>{selectedTask.description}</div>
                  </div>
                )}
                <div style={{ marginBottom: '0.75rem' }}>
                  <div style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.25rem' }}>Priority</div>
                  <span style={{
                    padding: '0.25rem 0.75rem',
                    borderRadius: '12px',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    backgroundColor: 
                      selectedTask.priority === 'urgent' ? '#dc3545' :
                      selectedTask.priority === 'high' ? '#fd7e14' :
                      selectedTask.priority === 'medium' ? '#ffc107' : '#28a745',
                    color: selectedTask.priority === 'medium' ? '#000' : '#fff'
                  }}>
                    {selectedTask.priority}
                  </span>
                </div>
                <div style={{ marginBottom: '0.75rem' }}>
                  <div style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.25rem' }}>Category</div>
                  <div style={{ fontSize: '1rem' }}>{selectedTask.category || 'N/A'}</div>
                </div>
                <div style={{ marginBottom: '0.75rem' }}>
                  <div style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.25rem' }}>Due Date</div>
                  <div style={{ fontSize: '1rem' }}>
                    {selectedTask.dueDate?.toDate ? selectedTask.dueDate.toDate().toLocaleDateString() : 'No due date'}
                  </div>
                </div>
                <div style={{ marginBottom: '0.75rem' }}>
                  <div style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.5rem' }}>Update Status</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <button
                      onClick={() => handleStatusUpdate(selectedTask.id, 'not_started')}
                      disabled={selectedTask.status === 'not_started'}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: selectedTask.status === 'not_started' ? '#6c757d' : '#e9ecef',
                        color: selectedTask.status === 'not_started' ? '#fff' : '#000',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: selectedTask.status === 'not_started' ? 'default' : 'pointer',
                        fontSize: '0.875rem'
                      }}
                    >
                      Not Started
                    </button>
                    <button
                      onClick={() => handleStatusUpdate(selectedTask.id, 'in_progress')}
                      disabled={selectedTask.status === 'in_progress'}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: selectedTask.status === 'in_progress' ? '#ffc107' : '#e9ecef',
                        color: selectedTask.status === 'in_progress' ? '#000' : '#000',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: selectedTask.status === 'in_progress' ? 'default' : 'pointer',
                        fontSize: '0.875rem'
                      }}
                    >
                      In Progress
                    </button>
                    <button
                      onClick={() => handleStatusUpdate(selectedTask.id, 'completed')}
                      disabled={selectedTask.status === 'completed'}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: selectedTask.status === 'completed' ? '#28a745' : '#e9ecef',
                        color: selectedTask.status === 'completed' ? '#fff' : '#000',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: selectedTask.status === 'completed' ? 'default' : 'pointer',
                        fontSize: '0.875rem'
                      }}
                    >
                      Completed
                    </button>
                    <button
                      onClick={() => handleStatusUpdate(selectedTask.id, 'rejected')}
                      disabled={selectedTask.status === 'rejected'}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: selectedTask.status === 'rejected' ? '#dc3545' : '#e9ecef',
                        color: selectedTask.status === 'rejected' ? '#fff' : '#000',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: selectedTask.status === 'rejected' ? 'default' : 'pointer',
                        fontSize: '0.875rem'
                      }}
                    >
                      Rejected
                    </button>
                  </div>
                </div>
                {selectedTask.createdAt && (
                  <div style={{ marginBottom: '0.75rem' }}>
                    <div style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.25rem' }}>Assigned</div>
                    <div style={{ fontSize: '1rem' }}>
                      {selectedTask.createdAt?.toDate ? selectedTask.createdAt.toDate().toLocaleString() : 'N/A'}
                    </div>
                  </div>
                )}
              </div>

              {/* Attachments Section */}
              <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid #dee2e6' }}>
                <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#495057' }}>Attachments</h3>
                {selectedTask.attachments && selectedTask.attachments.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
                    {selectedTask.attachments.map((attachment) => (
                      <div key={attachment.id} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '0.75rem',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '4px',
                        border: '1px solid #dee2e6'
                      }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>{attachment.fileName}</div>
                          <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.25rem' }}>
                            {(attachment.fileSize / 1024).toFixed(2)} KB
                            {attachment.uploadedByName && ` • Uploaded by ${attachment.uploadedByName}`}
                            {attachment.uploadedAt && attachment.uploadedAt.toDate && (
                              ` • ${attachment.uploadedAt.toDate().toLocaleDateString()}`
                            )}
                          </div>
                        </div>
                        <a
                          href={attachment.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: '#007bff',
                            color: 'white',
                            textDecoration: 'none',
                            borderRadius: '4px',
                            fontSize: '0.875rem',
                            marginLeft: '1rem'
                          }}
                        >
                          Download
                        </a>
                      </div>
                    ))}
                  </div>
                )}
                <div>
                  <input
                    type="file"
                    multiple
                    disabled={uploadingAttachment}
                    onChange={async (e) => {
                      if (e.target.files && e.target.files.length > 0 && selectedTask) {
                        try {
                          setUploadingAttachment(true);
                          const currentUser = auth.currentUser;
                          if (!currentUser) {
                            alert('You must be signed in to upload attachments');
                            return;
                          }

                          const files = Array.from(e.target.files);
                          const newAttachments: TaskAttachment[] = [];
                          
                          for (const file of files) {
                            const formData = new FormData();
                            formData.append('file', file);
                            formData.append('clientId', currentUser.uid);
                            formData.append('type', 'documents');

                            const response = await fetch('https://helpkeepmymoney.com/upload.php', {
                              method: 'POST',
                              body: formData,
                            });

                            if (!response.ok) {
                              throw new Error(`Failed to upload file: ${file.name}`);
                            }

                            const result = await response.json();
                            
                            let userName = currentUser.displayName || currentUser.email || 'Unknown';
                            try {
                              const userDocRef = doc(db, 'users', currentUser.uid);
                              const userDocSnap = await getDoc(userDocRef);
                              if (userDocSnap.exists()) {
                                const userData = userDocSnap.data();
                                userName = (userData.firstName && userData.lastName ? `${userData.firstName} ${userData.lastName}` : userData.displayName) || userName;
                              }
                            } catch (err) {
                              // Fallback to displayName or email
                            }

                            newAttachments.push({
                              id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
                              url: result.fileUrl,
                              fileName: result.originalName || file.name,
                              fileSize: result.size || file.size,
                              uploadedBy: currentUser.uid,
                              uploadedByName: userName,
                              uploadedAt: new Date()
                            });
                          }

                          // Update task with new attachments
                          const taskRef = doc(db, 'tasks', selectedTask.id);
                          const currentAttachments = selectedTask.attachments || [];
                          await updateDoc(taskRef, {
                            attachments: [...currentAttachments, ...newAttachments],
                            updatedAt: new Date()
                          });

                          await loadTasks();
                          // Update selectedTask to show new attachments
                          setSelectedTask(prev => prev ? {
                            ...prev,
                            attachments: [...currentAttachments, ...newAttachments]
                          } : null);
                          e.target.value = ''; // Clear file input
                        } catch (error: any) {
                          alert(`Failed to upload attachments: ${error.message}`);
                        } finally {
                          setUploadingAttachment(false);
                        }
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: '0.875rem'
                    }}
                  />
                  {!uploadingAttachment && (
                    <small style={{ display: 'block', marginTop: '0.25rem', color: '#6c757d', fontSize: '0.875rem' }}>
                      Select files to attach (Max 10MB per file)
                    </small>
                  )}
                </div>
              </div>

              {/* Comments Section */}
              <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid #dee2e6' }}>
                <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#495057' }}>Comments & Notes</h3>
                
                <div style={{ marginBottom: '1rem', maxHeight: '300px', overflowY: 'auto' }}>
                  {selectedTask.comments && selectedTask.comments.length > 0 ? (
                    selectedTask.comments.map((comment, index) => (
                      <div key={index} style={{
                        marginBottom: '1rem',
                        padding: '0.75rem',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '4px'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                          <strong style={{ fontSize: '0.875rem' }}>{comment.userName || 'Unknown'}</strong>
                          <span style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                            {(() => {
                              if (!comment.createdAt) return 'N/A';
                              // Handle Firestore Timestamp
                              if (comment.createdAt.toDate && typeof comment.createdAt.toDate === 'function') {
                                return comment.createdAt.toDate().toLocaleString();
                              }
                              // Handle JavaScript Date object
                              if (comment.createdAt instanceof Date) {
                                return comment.createdAt.toLocaleString();
                              }
                              // Handle date string or number (timestamp)
                              try {
                                const date = new Date(comment.createdAt);
                                if (!isNaN(date.getTime())) {
                                  return date.toLocaleString();
                                }
                              } catch (e) {
                                // fall through to N/A
                              }
                              return 'N/A';
                            })()}
                          </span>
                        </div>
                        <div style={{ fontSize: '0.875rem' }}>{comment.comment}</div>
                      </div>
                    ))
                  ) : (
                    <p style={{ color: '#6c757d', fontSize: '0.875rem' }}>No comments yet.</p>
                  )}
                </div>

                <div>
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment or note..."
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: '0.875rem',
                      fontFamily: 'inherit',
                      marginBottom: '0.5rem'
                    }}
                  />
                  <button
                    onClick={handleAddComment}
                    disabled={!newComment.trim()}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: newComment.trim() ? '#007bff' : '#ccc',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: newComment.trim() ? 'pointer' : 'not-allowed',
                      fontSize: '0.875rem'
                    }}
                  >
                    Add Comment
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface StatCardProps {
  title: string;
  value: number;
  color: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, color }) => (
  <div style={{
    backgroundColor: 'white',
    padding: '1rem',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    borderLeft: `4px solid ${color}`
  }}>
    <div style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.5rem' }}>
      {title}
    </div>
    <div style={{ fontSize: '2rem', fontWeight: 'bold', color }}>
      {value}
    </div>
  </div>
);

export default ClientTasks;

